/**
 * Getting an access token.
 *
 * Two modes, because the local development issuer is not a real authorization server:
 *
 * - `msal`  — Entra, Authorization Code + PKCE in the browser. What production uses.
 * - `dev`   — fetches a token from scripts/dev_idp.py. No redirect flow exists to perform,
 *             so MSAL would have nothing to talk to.
 *
 * Signing in starts from the /signin page in both modes -- nothing auto-redirects to Entra just
 * because an API call happened. Signing out lands on /signed-out (in msal mode that URL must be
 * registered as a post-logout redirect URI on the SPA app registration).
 *
 * The token cache lives in localStorage so a sign-in carries across tabs and browser restarts.
 * MSAL's default is sessionStorage, and this kept it until 2026-09-14, which meant every new
 * tab landed on the sign-in page. The trade-off is narrower than it sounds. Any script that
 * gets onto the origin can read either store, so the storage choice never prevented theft. It
 * only bounded how long a stolen refresh token stayed useful, and Entra caps SPA refresh tokens
 * at 24 hours either way. What keeps scripts off the page is DOMPurify on rendered answers and
 * the Content-Security-Policy that vite.config.ts writes into index.html. MSAL's temporary
 * state, the PKCE verifier and request id, stays in sessionStorage regardless of this setting.
 * See docs/adr/0002-entra-is-the-authorization-server.md.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from "@azure/msal-browser";

const MODE = (import.meta.env.VITE_AUTH_MODE ?? "dev") as "msal" | "dev";
const API_SCOPE = import.meta.env.VITE_API_SCOPE ?? "";

/** Thrown when a token is needed but nobody is signed in; the router sends these to /signin. */
export class NotSignedInError extends Error {
  constructor() {
    super("Not signed in.");
  }
}

let msal: PublicClientApplication | null = null;
let account: AccountInfo | null = null;

async function msalClient(): Promise<PublicClientApplication> {
  if (msal) return msal;
  const tenant = import.meta.env.VITE_ENTRA_TENANT_ID;
  const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
  if (!tenant || !clientId) {
    throw new Error(
      "VITE_ENTRA_TENANT_ID and VITE_ENTRA_CLIENT_ID are required when VITE_AUTH_MODE=msal.",
    );
  }
  msal = new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenant}`,
      redirectUri: window.location.origin,
    },
    cache: {
      // See the note at the top of this file.
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
  });
  await msal.initialize();
  // Completes a redirect we came back from, if any.
  const redirectResult = await msal.handleRedirectPromise();
  account = redirectResult?.account ?? msal.getAllAccounts()[0] ?? null;
  return msal;
}

/** Whether someone is signed in. In msal mode this also completes a redirect return. */
export async function isSignedIn(): Promise<boolean> {
  if (MODE === "dev") return localStorage.getItem(DEV_TOKEN_KEY) != null;
  await msalClient();
  return account != null;
}

/**
 * Starts an interactive sign-in. In msal mode this navigates away to Microsoft and, after the
 * round trip, lands on `returnTo`; in dev mode it resolves in place and the caller navigates.
 */
export async function signIn(returnTo = "/chat"): Promise<void> {
  if (MODE === "dev") {
    await devToken();
    return;
  }
  const client = await msalClient();
  if (account) return;
  await client.loginRedirect({
    scopes: [API_SCOPE],
    redirectStartPage: window.location.origin + returnTo,
  });
}

export async function signOut(): Promise<void> {
  if (MODE === "dev") {
    localStorage.removeItem(DEV_TOKEN_KEY);
    sessionStorage.clear();
    window.location.assign("/signed-out");
    return;
  }
  const client = await msalClient();
  // /signed-out must be registered as a post-logout redirect URI, or Entra ignores it and shows
  // its own generic signed-out page instead.
  await client.logoutRedirect({
    postLogoutRedirectUri: `${window.location.origin}/signed-out`,
  });
}

const DEV_TOKEN_KEY = "cortex.dev.token";

async function devToken(): Promise<string> {
  // localStorage, like the MSAL cache, so dev mode behaves like prod across tabs.
  const cached = localStorage.getItem(DEV_TOKEN_KEY);
  if (cached) return cached;
  const idp = import.meta.env.VITE_DEV_IDP ?? "http://localhost:9400";
  const email = import.meta.env.VITE_DEV_EMAIL ?? "dev@example.com";
  const response = await fetch(`${idp}/mint?email=${encodeURIComponent(email)}`);
  if (!response.ok) {
    throw new Error(
      `Could not get a token from the dev issuer at ${idp}. Is it running? ` +
        `(docker compose -f docker-compose.dev.yml up)`,
    );
  }
  const { access_token } = await response.json();
  localStorage.setItem(DEV_TOKEN_KEY, access_token);
  return access_token;
}

/** A bearer token for the API. Assumes the route guard has already ensured a sign-in. */
export async function getToken(): Promise<string> {
  if (MODE === "dev") return devToken();

  const client = await msalClient();
  if (!account) throw new NotSignedInError();
  try {
    const result = await client.acquireTokenSilent({ scopes: [API_SCOPE], account });
    return result.accessToken;
  } catch (e) {
    // Silent acquisition fails once the refresh token is spent or consent is needed. This is
    // expected, not exceptional -- the only fix is to ask the user.
    if (e instanceof InteractionRequiredAuthError) {
      await client.acquireTokenRedirect({ scopes: [API_SCOPE], account });
      throw new Error("Redirecting to sign in.");
    }
    throw e;
  }
}

export function authMode(): string {
  return MODE;
}
